"use client";
/**
 * Island — the what-is-going-on panel. Not a floating card: the composer pill
 * itself unrolls (fused radius) and the fleet lives inside the same object.
 *
 * Rows: status + age on the left, title/subtitle in a two-line middle, and on
 * the right an action button only when the row owes the user something (the
 * interaction's own actions via deriveNeedCard — one black primary, grey
 * secondaries). The whole row is the door (founder 2026-09-02): click
 * anywhere on it to open the task. Needs-you rows resolve RIGHT HERE on the
 * same rail the home board and the dots use (deriveNeedCard +
 * resolveInteractionApi) — a renderer of that truth, never a
 * re-implementation.
 *
 * Keyboard while open: ↑↓ select, Enter fires the primary action else opens,
 * ←→ cycle filters. Closing is deliberate only (esc / click-outside / ⌥ — the
 * host owns those); hover-away never closes.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  ANALYTICS_EVENTS,
  analyticsInteractionKindOf,
  deriveNeedCard,
  type InteractionOutcome,
  type NeedCardAction,
  type NeedCardModel,
} from "@bash/shared";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { resolveInteractionApi } from "@/lib/api/interactions";
import { useTaskStore } from "@/stores/taskStore";
import { captureV1, resolutionOf } from "@/lib/analytics-v1";
import { isDockWindow } from "@/lib/dock-window";
import { NeedCardAnswer } from "@/components/home-briefing/NeedCardAnswer";
import Tabs from "@/components/atoms/tabs";
import type { FleetRow } from "./fleet";
import type { FleetPending } from "./useFleet";
import {
  CHIP,
  FILTERS,
  filterCounts,
  keyboardStep,
  visibleRows,
  type IslandFilter,
} from "./islandModel";
import { useDockRouter } from "./live/useDockRouter";

/** The durable `kind` of the interaction behind a card: the field the server's interaction:requested read. */
const interactionKindIn = (rows: FleetRow[], id: string): string | undefined =>
  rows
    .map((r) => (r.interaction as FleetPending | null)?.row)
    .find((row) => row?.id === id)?.kind;

interface IslandProps {
  rows: FleetRow[];
  open: boolean;
  onClose: () => void;
  /** The element the island unrolls from (the dock root): the popover anchors to
   *  its box, above it, at its width. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Tray-without-voice (done rail phase 3): the empty-state drops the hold-to-speak hint. */
  quiet?: boolean;
  /** Done rail phase 3: newest creation per task — landed rows show the thing itself. */
  thumbs?: Map<string, { url: string }>;
}

export function Island({
  rows,
  open,
  onClose,
  anchorRef,
  quiet,
  thumbs,
}: IslandProps) {
  // In the shell's Dock window a push goes to the main window (useDockRouter).
  const router = useDockRouter();
  const [filter, setFilter] = useState<IslandFilter>("all");
  const [selIx, setSelIx] = useState(0);
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setFilter("all");
      setSelIx(0);
    }
  }, [open]);

  const vis = visibleRows(rows, filter);
  const clampedSel = Math.min(selIx, Math.max(0, vis.length - 1));
  const filters = useMemo(
    () => FILTERS.map((f) => ({ ...f, badge: filterCounts(rows)[f.id] })),
    [rows]
  );

  const cardFor = useCallback((row: FleetRow): NeedCardModel | null => {
    const pend = row.interaction as FleetPending | null;
    if (!pend?.row) return null;
    const r = pend.row;
    // A realtime payload can omit a TOASTed `request`; deriving a card from
    // nothing takes the whole surface down, so this row simply has no card.
    if (!r.request) return null;
    return deriveNeedCard(r.request, {
      interactionId: r.id,
      taskId: r.task_id,
      taskTitle: r.tasks?.title ?? row.title,
      createdAt: r.created_at,
    });
  }, []);

  const openTask = useCallback(
    (taskId: string) => {
      onClose();
      router.push(`/task/${taskId}`);
    },
    [onClose, router]
  );

  // Byte-identical resolution rail to the home board and the dots strip.
  const resolveInPlace = useCallback(
    async (card: NeedCardModel, outcome: InteractionOutcome) => {
      if (!card.interactionId) return;
      // Read BEFORE the resolve: the realtime feed retires the row once it lands.
      const kind = interactionKindIn(rows, card.interactionId);
      setBusyIds((prev) => new Set(prev).add(card.id));
      setRowErrors((prev) => ({ ...prev, [card.id]: "" }));
      try {
        await resolveInteractionApi(card.interactionId, outcome);
        // Analytics v1: interaction:resolved replaced home:need_resolved.
        // The same kind the server's interaction:requested sent for this row.
        captureV1(ANALYTICS_EVENTS.INTERACTION_RESOLVED, {
          interaction_kind: analyticsInteractionKindOf(kind),
          resolution: resolutionOf(outcome),
          interaction_id: card.interactionId,
          ...(card.taskId ? { task_id: card.taskId } : {}),
        });
        setAnsweringId(null);
        // The realtime feed retires the row; the fleet re-derives itself.
      } catch {
        setRowErrors((prev) => ({
          ...prev,
          [card.id]: "That didn’t go through. Try again.",
        }));
      } finally {
        setBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(card.id);
          return next;
        });
      }
    },
    [rows]
  );

  const handleAction = useCallback(
    (card: NeedCardModel) => (action: NeedCardAction) => {
      const intent = action.intent;
      if (intent.type === "resolve") void resolveInPlace(card, intent.outcome);
      else if (intent.type === "answer")
        setAnsweringId((prev) => (prev === card.id ? null : card.id));
      // oauth / proposals need the full home machinery — the task page has it.
      else if (card.taskId) openTask(card.taskId);
    },
    [openTask, resolveInPlace]
  );

  // The island owns the keyboard while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // The shell's dock window hands the keyboard back on an unclaimed
        // Escape; this one folded the island, so it is claimed (spec §4).
        if (isDockWindow()) e.preventDefault();
        onClose();
        return;
      }
      const step = keyboardStep(
        { selIx: clampedSel, filter },
        e.key,
        vis.length
      );
      if (step) {
        e.preventDefault();
        setFilter(step.filter);
        setSelIx(step.selIx);
        requestAnimationFrame(() =>
          listRef.current
            ?.querySelector(".cv2-i-row.sel")
            ?.scrollIntoView({ block: "nearest" })
        );
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const rowSel = vis[clampedSel];
        if (!rowSel) return;
        const card = cardFor(rowSel);
        const primary = card?.actions.find((a) => a.primary);
        if (card && primary) handleAction(card)(primary);
        else openTask(rowSel.id);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, clampedSel, filter, vis, cardFor, handleAction, openTask, onClose]);

  return (
    // Controlled with no onOpenChange on purpose: closing is the host's
    // (esc / click-outside / ⌥), so the popover's own dismissal is inert.
    <Popover open={open}>
      <PopoverAnchor virtualRef={anchorRef as RefObject<HTMLElement>} />
      <PopoverContent
        side="top"
        align="center"
        sideOffset={10}
        avoidCollisions={false}
        // The composer keeps the caret: the island never takes or returns focus.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="cv2-island open w-[var(--radix-popover-trigger-width)] pt-1 px-1.5 pb-1.5 backdrop-blur-[22px] backdrop-saturate-[1.4]"
        data-testid="cv2-island"
      >
        {rows.length === 0 ? (
          <div className="pt-4 px-2.5 pb-4.5 text-3.5 text-fg-muted text-center">
            {quiet ? "All quiet." : "All quiet. Hold ⌃ and speak."}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3.5 pt-2.5 sm:px-3 pb-2">
              <span className="hidden sm:inline-block text-3 font-semibold tracking-wider uppercase text-fg-muted">
                In flight
              </span>
              <Tabs
                layout={2}
                active={filter}
                items={filters}
                wrapperClass="max-sm:mr-auto ml-auto flex-none"
                onTabChange={setFilter}
              />
            </div>
            <div
              className="max-h-54.5 min-h-44 overflow-y-auto pt-0 px-0.5 pb-0.5"
              ref={listRef}
            >
              {vis.map((row, i) => {
                const card = cardFor(row);
                const busy = card ? busyIds.has(card.id) : false;
                const err = card ? rowErrors[card.id] : undefined;
                return (
                  <div
                    key={row.id}
                    className={cn(
                      "cv2-i-row mb-2 flex items-center gap-2.5 py-1.5 px-2.5 max-sm:border-l-3 sm:rounded-l-[11px] rounded-r-[11px] hover:bg-cv2-hover cursor-pointer",
                      i === clampedSel ? " sel bg-cv2-hover" : "",
                      CHIP[row.state].bcls
                    )}
                    data-s={row.state}
                    data-testid="cv2-i-row"
                    // The whole row is the door (founder 09-02); the action pills
                    // and the arrow stop propagation and keep their own jobs.
                    onClick={() => {
                      setSelIx(i);
                      openTask(row.id);
                    }}
                  >
                    <span className="flex-none w-19 hidden sm:flex flex-col gap-0.5">
                      <span
                        className={cn(
                          "text-[9.5px] font-bold tracking-wider",
                          CHIP[row.state].cls
                        )}
                      >
                        {CHIP[row.state].word}
                      </span>
                      {row.ago ? (
                        <span className="text-2.5 text-fg-faint">
                          {row.ago}
                        </span>
                      ) : null}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-3.5 font-semibold text-fg-default">
                        {card?.title || row.title}
                      </div>
                      <div className="text-3 text-fg-muted mt-px whitespace-nowrap overflow-hidden text-ellipsis">
                        {card?.why || row.sub}
                      </div>
                      {card && answeringId === card.id && card.questions ? (
                        <NeedCardAnswer
                          questions={card.questions}
                          busy={busy}
                          onSubmit={(answers) => {
                            // Done rail (08-22): a verdict-minted ask never rides
                            // the continue rail — the answer is a real task
                            // message; the resolve is the audit trail (mirrors
                            // NeedsYouSection.handleAnswer).
                            if (card.answerAsMessage && card.taskId) {
                              const text = Object.values(answers)
                                .filter(Boolean)
                                .join("\n")
                                .trim();
                              if (text) {
                                const store = useTaskStore.getState();
                                store._setSelectedTaskId(card.taskId);
                                store.sendMessage(text);
                              }
                              void resolveInPlace(card, {
                                behavior: "allow",
                                updatedInput: {
                                  questions: card.questions ?? [],
                                  answers,
                                },
                              });
                              openTask(card.taskId);
                              return;
                            }
                            void resolveInPlace(card, {
                              behavior: "allow",
                              updatedInput: {
                                questions: card.questions ?? [],
                                answers,
                              },
                            });
                          }}
                          onCancel={() => setAnsweringId(null)}
                        />
                      ) : null}
                      {err ? (
                        <div className="text-3 text-cv2-chip-error pt-1">
                          {err}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex-none flex items-center gap-1.5">
                      {row.state === "done" && thumbs?.get(row.id) ? (
                        <img
                          className="w-10 h-7.5 rounded-6 [object-fit:cover] flex-none border border-border-subtle"
                          src={thumbs.get(row.id)!.url}
                          alt=""
                          loading="lazy"
                          data-testid="cv2-i-thumb"
                        />
                      ) : null}
                      {card?.actions.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          disabled={busy}
                          className={cn(
                            "cv2-i-act text-[11.5px] font-semibold rounded-8 py-1.25 px-2.75 border-none cursor-pointer [font-family:inherit] transition-[background] duration-150 ease-out disabled:opacity-[0.55] disabled:cursor-default",
                            a.primary
                              ? " pri bg-fg-default text-bg-base hover:opacity-[0.88]"
                              : " bg-[color-mix(in_srgb,var(--fg-default)_8%,transparent)] text-fg-default hover:bg-[color-mix(in_srgb,var(--fg-default)_13%,transparent)]"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(card)(a);
                          }}
                        >
                          {busy && a.primary ? "Working…" : a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {vis.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-3.5 text-fg-muted text-center">
                  Nothing here right now.
                </div>
              ) : null}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
